import React, { useEffect, useRef, useState } from "react";
import {
  averageAllNumbersOfObject,
  sumAllNumbersOfObject,
} from "../../../../util/dealingArrObj";
import { useUIContext } from "../../../../context/UIContext";
import PromptModal from "../../../../unicomponents/PromptModal";
import { useAuthContext } from "../../../../context/AuthContext";
import useFlexibleData from "../../../../hooks/useFlexibleData";
import useUnsavedChangesWarning from "../../../../hooks/useUnsavedChangesWarning";
import { gradeToConvert100 } from "../../../../util/specialCase";
import { useTranslation } from "react-i18next";
import {
  validateTextLength,
  getTextLengthPlaceholder,
} from "../../../../util/textLengthValidator";
import styles from "../css/LessonStudentsTable.module.css";

export default function LessonStudentsItemsTable({
  lessonStudentsData,
  setLessonStudentsData,
  lessonSetting,
  updateLessonStudents,
  updateLessonStudentsBulk,
  deleteLessonStudents,
  lessonInfo,
  evalItemsData,
  showEvalPlan = false,
}) {
  /*
생각해 보자. level1 행을 찾아서 td 1개 만든다. 
그 level1을 parent_id를 갖고 있는 level2를 찾아서 그 옆에 td를 1번 추가한다. 없으면 빈 td 2개 추가한다.
 parent_id가 같은 level2가 또 있으면 tr를 1개 추가한다. 앞에 td는 빈칸 
추가된 level2를 parent_id를 갖고 있는 level3를 찾아서 그 옆에 td를 추가한다. 없으면 빈 td 1개 추가한다.
 parent_id가 같은 level3가 또 있으면 tr를 1개 추가한다. 앞에 td 2개는 빈칸 

좋아 됐다.

이제 해야 할 것은..
1. 저장된 성적이 표시되도록
2. 정보 입력 하는 것에 따라서 학생별로 state에 기록되도록
3. 저장버튼 누르면 해당 학생의 성적이 create (저장) 또는 업데이트 되도록
4. 모두 저장 누르면 수정 경험이 있는 애들 것 모두 저장 되도록...
5. 기타 스타일 좀 손 보고.... input, select 등 eval type에 따라서 다르게 나오는 것 하면 되겠군

 */

  const { t } = useTranslation();

  const { setIsLoadingModal, handleToastCenterTop, handleToastCenterBottom } =
    useUIContext();
  const [savedRows, setSavedRows] = useState([]);
  const [savedBulk, setSavedBulk] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [dataForPromptModal, setDataForPromptModal] = useState({});
  const [confirmDelete, setConfirmDelete] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // 저장 안 된 변경사항 경고
  useUnsavedChangesWarning(hasUnsavedChanges);

  // 사용자가 실제로 타이핑한 eval_item_grades 값을 동기적으로 보관.
  // React state 갱신이 stale closure나 batching으로 누락되는 경우를 대비한
  // shadow source of truth. 저장 직전 state와 비교하여 누락을 감지/복구한다.
  // 키 구조: { [username]: { [evalItemId]: value } }
  const typedShadowRef = useRef({});

  // 수업이 바뀌면 이전 수업의 shadow 데이터는 무의미하므로 비움
  useEffect(() => {
    typedShadowRef.current = {};
  }, [lessonInfo?.id]);

  const { user, hasPermission } = useAuthContext();
  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({
    schoolid: lessonSetting[0]?.schoolid,
    main_category: "convert100",
  });

  const {
    flexibleDataQuery: { data: schoolLifeflexibleData },
  } = useFlexibleData({
    schoolid: lessonSetting[0]?.schoolid,
    main_category: "schoolLifeRecord",
  });

  const {
    flexibleDataQuery: { data: extraSettingsData },
  } = useFlexibleData({
    schoolid: lessonSetting[0]?.schoolid,
    main_category: "extraSettings",
  });

  const textLengthRecord = extraSettingsData?.find(
    (r) => r.sub_category === "textLength",
  );
  const textLengthSettings = textLengthRecord?.data_value;

  const convertCondition =
    lessonInfo && lessonInfo.extra_attributes?.subjectSort === "P/F"
      ? "P/F"
      : lessonInfo.hierarchyOne;

  const hasUpdatePermission = () => {
    const isOwner = lessonInfo?.teachers?.some(
      (teacher) => teacher.username === user.username,
    );
    return isOwner || hasPermission(null, "can_update");
  };

  const hasDeletePermission = () => {
    const isOwner = lessonInfo?.teachers?.some(
      (teacher) => teacher.username === user.username,
    );
    return isOwner || hasPermission(null, "can_delete");
  };

  const initiatePromptModal = (data) => {
    setModalOpen(true);
    setDataForPromptModal(data);
  };

  const handlePromptModalSubmit = (dataForPromptModal) => {
    const { id, keyName, title, upperKey, ...updatedData } = dataForPromptModal;

    setLessonStudentsData((currentDataSets) =>
      currentDataSets.map((dataSet) => {
        if (dataSet.id !== id) return dataSet;

        if (upperKey) {
          return {
            ...dataSet,
            [upperKey]: {
              ...dataSet[upperKey],
              ...updatedData,
            },
            isModified: true,
          };
        }

        return { ...dataSet, ...updatedData, isModified: true };
      }),
    );
    setHasUnsavedChanges(true);
  };

  const closeModal = () => setModalOpen(false);
  const achievementLabel = lessonInfo?.extra_attributes?.achievement;
  const achievementConvert100Setting = flexibleData?.find(
    (r) => r.main_category === "convert100" && r.sub_category === "achievement",
  )?.data_value;
  const evalItemConvert100Setting = flexibleData?.find(
    (r) => r.main_category === "convert100" && r.sub_category === "평가항목별",
  )?.data_value;
  const selectedConvert100Setting = achievementLabel
    ? achievementConvert100Setting || evalItemConvert100Setting
    : evalItemConvert100Setting;
  const manualConvert100Enabled =
    selectedConvert100Setting?.allowManualConvert100 === true ||
    evalItemConvert100Setting?.allowManualConvert100 === true;
  const shouldShowConvert100Row =
    selectedConvert100Setting || manualConvert100Enabled;

  // 일반적인 row 데이터 변경을 처리하는 함수
  const handleTdChange = (username, evalItemId, event) => {
    const { value } = event.target;
    const field = event.target.name;
    const hasBadInput = event.target.validity?.badInput === true;
    // checkbox용 값은 setState 콜백 시점에 event.target.checked에 접근하지 못할 수
    // 있으므로 미리 추출해둔다 (React event pooling 대비).
    const checked = event.target.checked;

    //lessonid와 username으로 찾은 후 eval_items_grades에서 evalItems id를 key 값으로
    if (field === "eval_item_grades") {
      // 100점제에서는 숫자 입력만 허용한다.
      // 브라우저 number input에서 잘못된 문자가 들어오면 badInput이 true가 되는데,
      // 이 경우 빈 문자열로 덮어써 기존 정상값이 유실될 수 있으므로 즉시 차단한다.
      if (checkEvalTypes(["100점제"]) && hasBadInput) {
        handleToastCenterBottom(
          "숫자만 입력 가능합니다. (소수점, 음수, 빈칸, 0 허용)",
        );
        return;
      }

      // 사용자가 실제로 타이핑한 값을 shadow ref에 동기 기록.
      // 이후 stale closure로 인해 state 갱신이 유실되어도 저장 직전 비교로
      // 복구할 수 있다.
      if (!typedShadowRef.current[username]) {
        typedShadowRef.current[username] = {};
      }
      typedShadowRef.current[username][evalItemId] = value;

      setLessonStudentsData((prevRows) =>
        prevRows.map((row) => {
          if (row.lesson_id !== lessonInfo.id || row.username !== username) {
            return row;
          }
          const prevGrades = row.eval_item_grades || {};
          const newGrades = { ...prevGrades, [evalItemId]: value };
          const updatedStudent = { ...row, eval_item_grades: newGrades };
          const newGrade = calculateGradeForStudent(updatedStudent);
          return {
            ...row,
            eval_item_grades: newGrades,
            grade: newGrade,
            convert100Override: undefined,
            convert100: calculateConvert100(updatedStudent, newGrade),
            isModified: true,
          };
        }),
      );
      setHasUnsavedChanges(true);
    } else if (field === "extra_data") {
      setLessonStudentsData((prevRows) =>
        prevRows.map((row) => {
          if (row.lesson_id !== lessonInfo.id || row.username !== username) {
            return row;
          }
          const prev = row.extra_data || {};
          const nextValue =
            evalItemId === "statExclude" ? (checked ? 1 : 0) : value;
          return {
            ...row,
            extra_data: { ...prev, [evalItemId]: nextValue },
            isModified: true,
          };
        }),
      );
      setHasUnsavedChanges(true);
    }
  };

  // 태그 변경을 처리하는 함수
  const handleTagChange = (tagName, event, username, tagIndex) => {
    const { value } = event.target; // 선택된 새로운 태그 값
    setLessonStudentsData((prevRows) =>
      prevRows.map((row) => {
        if (row.lesson_id !== lessonInfo.id || row.username !== username) {
          return row;
        }
        const updatedTags = (row.tags || []).map((tag, idx) =>
          idx === tagIndex ? { [tagName]: value } : tag,
        );
        return { ...row, tags: updatedTags, isModified: true };
      }),
    );
    setHasUnsavedChanges(true);
  };

  const isGradeError = (val) =>
    val === "숫자만 가능" || val === "N/A" || val === "숫자만가능";

  const shouldUseConvert100Override = (student, convert100) =>
    student.convert100Override != null &&
    student.convert100Override !== "" &&
    (manualConvert100Enabled || isGradeError(convert100));

  const handleManualGradeChange = (studentId, field, value) => {
    setLessonStudentsData((prev) =>
      prev.map((s) =>
        s.id === studentId
          ? { ...s, [`${field}Override`]: value, isModified: true }
          : s,
      ),
    );
    setHasUnsavedChanges(true);
  };

  function renderTd(
    evaluationType,
    eval_item_grades,
    evalItemId,
    username,
    student,
  ) {
    const uniqueKey = `${username}-${evalItemId}-${evaluationType}`;
    switch (evaluationType) {
      case "100점제":
        return (
          <td key={uniqueKey} className="text-center">
            <input
              className="input-like-span"
              type="number"
              name="eval_item_grades"
              onChange={(e) => handleTdChange(username, evalItemId, e)}
              value={eval_item_grades?.[evalItemId] ?? ""}
              autoComplete="off"
            />
            {lessonSetting
              ?.find((item) => item.item_code === "evalItem")
              ?.detailed_setting?.includes("피드백") && (
              <button
                className={`btn btn-sm ${
                  student?.extra_data?.[evalItemId]
                    ? "btn-outline-success"
                    : "btn-outline-secondary"
                } ps-1 p-0 m-0`}
                style={{
                  fontSize: "11px",
                  minWidth: "50px",
                  maxWidth: "60px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minHeight: "18px",
                }}
                onClick={(e) =>
                  initiatePromptModal({
                    id: student.id,
                    [evalItemId]: student?.extra_data?.[evalItemId],
                    keyName: evalItemId,
                    title: t("teacher.lessonRelated.feedback"),
                    upperKey: "extra_data",
                    fullname: student?.fullname,
                  })
                }
              >
                {student?.extra_data?.[evalItemId] ??
                  t("teacher.lessonRelated.feedback")}
              </button>
            )}
          </td>
        );

      case "이수제":
        return (
          <td key={uniqueKey} className="text-center">
            <select
              className="select-like-span"
              name="eval_item_grades"
              onChange={(e) => handleTdChange(username, evalItemId, e)}
              value={eval_item_grades?.[evalItemId] ?? ""}
            >
              <option></option>
              <option value="pass">{t("teacher.lessonRelated.pass")}</option>
              <option value="fail">{t("teacher.lessonRelated.fail")}</option>
            </select>
            {lessonSetting
              ?.find((item) => item.item_code === "evalItem")
              ?.detailed_setting?.includes("피드백") && (
              <button
                className={`btn btn-sm ${
                  student?.extra_data?.[evalItemId]
                    ? "btn-outline-success"
                    : "btn-outline-secondary"
                } ps-1 p-0 m-0`}
                style={{
                  fontSize: "11px",
                  minWidth: "50px",
                  maxWidth: "60px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minHeight: "18px",
                }}
                onClick={(e) =>
                  initiatePromptModal({
                    id: student.id,
                    [evalItemId]: student?.extra_data?.[evalItemId],
                    keyName: evalItemId,
                    title: t("teacher.lessonRelated.feedback"),
                    upperKey: "extra_data",
                    fullname: student?.fullname,
                  })
                }
              >
                {student?.extra_data?.[evalItemId] ??
                  t("teacher.lessonRelated.feedback")}
              </button>
            )}
          </td>
        );
      case "3단평가":
        return (
          <td key={uniqueKey} className="text-center">
            <select
              className="select-like-span"
              name="eval_item_grades"
              onChange={(e) => handleTdChange(username, evalItemId, e)}
              value={eval_item_grades?.[evalItemId] ?? ""}
            >
              <option value="3">{t("teacher.lessonRelated.good")}</option>
              <option value="2">{t("teacher.lessonRelated.normal")}</option>
              <option value="1">{t("teacher.lessonRelated.effort")}</option>
              <option value="0">{t("teacher.lessonRelated.notDone")}</option>
              <option value=""></option>
            </select>
            {lessonSetting
              ?.find((item) => item.item_code === "evalItem")
              ?.detailed_setting?.includes("피드백") && (
              <button
                className={`btn btn-sm ${
                  student?.extra_data?.[evalItemId]
                    ? "btn-outline-success"
                    : "btn-outline-secondary"
                } ps-1 p-0 m-0`}
                style={{
                  fontSize: "11px",
                  minWidth: "50px",
                  maxWidth: "60px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minHeight: "18px",
                }}
                onClick={(e) =>
                  initiatePromptModal({
                    id: student.id,
                    [evalItemId]: student?.extra_data?.[evalItemId],
                    keyName: evalItemId,
                    title: t("teacher.lessonRelated.feedback"),
                    upperKey: "extra_data",
                    fullname: student?.fullname,
                  })
                }
              >
                {student?.extra_data?.[evalItemId] ??
                  t("teacher.lessonRelated.feedback")}
              </button>
            )}
          </td>
        );

      case "ABC":
        return (
          <td key={uniqueKey} className="text-center">
            <select
              className="select-like-span"
              name="eval_item_grades"
              onChange={(e) => handleTdChange(username, evalItemId, e)}
              value={eval_item_grades?.[evalItemId] ?? ""}
            >
              <option value=""></option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
              <option value="F">F</option>
            </select>
            {lessonSetting
              ?.find((item) => item.item_code === "evalItem")
              ?.detailed_setting?.includes("피드백") && (
              <button
                className={`btn btn-sm ${
                  student?.extra_data?.[evalItemId]
                    ? "btn-outline-success"
                    : "btn-outline-secondary"
                } ps-1 p-0 m-0`}
                style={{
                  fontSize: "11px",
                  minWidth: "50px",
                  maxWidth: "60px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minHeight: "18px",
                }}
                onClick={(e) =>
                  initiatePromptModal({
                    id: student.id,
                    [evalItemId]: student?.extra_data?.[evalItemId],
                    keyName: evalItemId,
                    title: t("teacher.lessonRelated.feedback"),
                    upperKey: "extra_data",
                    fullname: student?.fullname,
                  })
                }
              >
                {student?.extra_data?.[evalItemId] ??
                  t("teacher.lessonRelated.feedback")}
              </button>
            )}
          </td>
        );
      case "직접입력":
        return (
          <td key={uniqueKey} className="text-center">
            <input
              className="input-like-span"
              type="text"
              name="eval_item_grades"
              onChange={(e) => handleTdChange(username, evalItemId, e)}
              value={eval_item_grades?.[evalItemId] ?? ""}
              autoComplete="off"
            />
            {lessonSetting
              ?.find((item) => item.item_code === "evalItem")
              ?.detailed_setting?.includes("피드백") && (
              <button
                className={`btn btn-sm ${
                  student?.extra_data?.[evalItemId]
                    ? "btn-outline-success"
                    : "btn-outline-secondary"
                } ps-1 p-0 m-0`}
                style={{
                  fontSize: "11px",
                  minWidth: "50px",
                  maxWidth: "60px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minHeight: "18px",
                }}
                onClick={(e) =>
                  initiatePromptModal({
                    id: student.id,
                    [evalItemId]: student?.extra_data?.[evalItemId],
                    keyName: evalItemId,
                    title: t("teacher.lessonRelated.feedback"),
                    upperKey: "extra_data",
                    fullname: student?.fullname,
                  })
                }
              >
                {student?.extra_data?.[evalItemId] ??
                  t("teacher.lessonRelated.feedback")}
              </button>
            )}
          </td>
        );
      case "5단평가":
        return (
          <td key={uniqueKey} className="text-center">
            <select
              className="select-like-span"
              name="eval_item_grades"
              onChange={(e) => handleTdChange(username, evalItemId, e)}
              value={eval_item_grades?.[evalItemId] ?? ""}
            >
              <option value="5">5</option>
              <option value="4">4</option>
              <option value="3">3</option>
              <option value="2">2</option>
              <option value="1">1</option>
              <option value="0">0</option>
              <option value=""></option>
            </select>
            {lessonSetting
              ?.find((item) => item.item_code === "evalItem")
              ?.detailed_setting?.includes("피드백") && (
              <button
                className={`btn btn-sm ${
                  student?.extra_data?.[evalItemId]
                    ? "btn-outline-success"
                    : "btn-outline-secondary"
                } ps-1 p-0 m-0`}
                style={{
                  fontSize: "11px",
                  minWidth: "50px",
                  maxWidth: "60px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minHeight: "18px",
                }}
                onClick={(e) =>
                  initiatePromptModal({
                    id: student.id,
                    [evalItemId]: student?.extra_data?.[evalItemId],
                    keyName: evalItemId,
                    title: t("teacher.lessonRelated.feedback"),
                    upperKey: "extra_data",
                    fullname: student?.fullname,
                  })
                }
              >
                {student?.extra_data?.[evalItemId] ??
                  t("teacher.lessonRelated.feedback")}
              </button>
            )}
          </td>
        );
      default:
        return (
          <td key={uniqueKey} className="text-center">
            {t("common.status.error")}
          </td>
        );
    }
  }

  const findItems = (parentId, level) =>
    evalItemsData.filter(
      (item) => item.hierarchy_level === level && item.parent_id === parentId,
    );

  const findByLevels = (level) =>
    evalItemsData.filter((item) => item.hierarchy_level === level);

  const checkEvalTypes = (evalTypes) =>
    evalTypes.some((evalType) =>
      evalItemsData.every((item) => item.evaluation_type === evalType),
    );

  // 100점제 숫자 입력 검증 (빈칸 허용)
  const isValidNumericGradeValue = (rawValue) => {
    if (rawValue === null || rawValue === undefined) return true;
    const normalized = String(rawValue).replace(/\u00A0/g, " ").trim();
    if (normalized === "") return true; // 빈칸 허용
    // 숫자, 소수점, 음수 허용 (예: 0, -1, 1.5, -.5)
    if (!/^-?(?:\d+\.?\d*|\.\d+)$/.test(normalized)) return false;
    return Number.isFinite(Number(normalized));
  };

  // 저장 직전 100점제 eval_item_grades 유효성 검사
  const findInvalidEvalItemGrades = (students) => {
    if (!checkEvalTypes(["100점제"])) return [];

    const invalidEntries = [];
    students.forEach((student) => {
      const grades = student?.eval_item_grades || {};
      Object.entries(grades).forEach(([evalItemId, rawValue]) => {
        if (!isValidNumericGradeValue(rawValue)) {
          const evalItem = evalItemsData.find(
            (item) => String(item.id) === String(evalItemId),
          );
          invalidEntries.push({
            fullname: student?.fullname,
            username: student?.username,
            evalItemId,
            evalItemName: evalItem?.content || evalItemId,
            value: rawValue,
          });
        }
      });
    });
    return invalidEntries;
  };

  const getEvalArea100Setting = () =>
    lessonSetting?.find((r) => r.item_code === "evalArea100")?.saved_setting;

  const isEvalItemTotalScoreEnabled = () =>
    lessonSetting
      ?.find((item) => item.item_code === "evalItem")
      ?.detailed_setting?.includes("만점");

  const hasPositiveExtraDataNumber = (key) =>
    evalItemsData?.some((item) => {
      const value = Number(item.extra_data?.[key] || 0);
      return Number.isFinite(value) && value > 0;
    });

  const shouldUseTotalOnlyScore = () =>
    checkEvalTypes(["100점제"]) &&
    isEvalItemTotalScoreEnabled() &&
    hasPositiveExtraDataNumber("total") &&
    !hasPositiveExtraDataNumber("weight");

  const getLeafEvalItems = (items) =>
    items.filter(
      (item) => !items.some((child) => child.parent_id === item.id),
    );

  const calculateTotalOnlyScore = (
    student,
    items,
    calculType = "",
    excludeEmptyGrade = false,
  ) => {
    const grades = student?.eval_item_grades || {};
    const leafItems = getLeafEvalItems(items || []);
    if (leafItems.length === 0) return "";

    let totalScore = 0;
    let totalPossible = 0;
    let percentageSum = 0;
    let includedCount = 0;

    for (const item of leafItems) {
      const rawScore = grades[item.id];
      const isEmptyScore =
        rawScore === "" || rawScore === null || rawScore === undefined;
      if (excludeEmptyGrade && isEmptyScore) continue;

      const score = isEmptyScore ? 0 : Number(rawScore);
      if (!Number.isFinite(score)) return "숫자만 가능";

      const total = Number(item.extra_data?.total || 100);
      if (!Number.isFinite(total) || total <= 0) continue;

      totalScore += score;
      totalPossible += total;
      percentageSum += (score / total) * 100;
      includedCount += 1;
    }

    if (includedCount === 0 || totalPossible <= 0) return "";

    const score =
      calculType === "maxAverage"
        ? percentageSum / includedCount
        : (totalScore / totalPossible) * 100;

    return Number(score.toFixed(2)).toString();
  };

  const calculateGradeForStudent = (student) => {
    if (
      checkEvalTypes(["100점제"]) &&
      getEvalArea100Setting() === "원점수기록"
    ) {
      return calculateTotalScore(student, evalItemsData) || "";
    }

    return sumAllNumbersOfObject(student?.eval_item_grades || {}) || "";
  };

  const calculateConvert100SourceScore = (student, grade) => {
    if (!shouldUseTotalOnlyScore()) return grade;

    const totalOnlyScore = calculateTotalOnlyScore(
      student,
      evalItemsData,
      evalItemConvert100Setting?.calculType || "",
      evalItemConvert100Setting?.excludeEmptyGrade || false,
    );

    return totalOnlyScore !== "" && !isGradeError(totalOnlyScore)
      ? totalOnlyScore
      : grade;
  };

  const calculateConvert100 = (student, grade) =>
    gradeToConvert100(
      calculateConvert100SourceScore(student, grade),
      selectedConvert100Setting,
      convertCondition,
      lessonInfo?.extra_attributes?.subjectCategory,
      lessonInfo?.extra_attributes?.achievement,
    );

  // 최종 level(leaf node) 항목 찾기 - 평가계획서 데이터 확인용
  const findFinalLevelItems = React.useMemo(() => {
    if (!evalItemsData || evalItemsData.length === 0) return [];

    const findItems = (parentId, level) =>
      evalItemsData.filter(
        (item) => item.hierarchy_level === level && item.parent_id === parentId,
      );

    const finalItems = [];
    const level1Items = findItems(0, 1);

    level1Items.forEach((level1) => {
      const level2Items = findItems(level1.id, 2);

      if (level2Items.length === 0) {
        // level 1만 있고 level 2가 없으면 → level 1이 최종
        finalItems.push({
          finalItem: level1,
          parentItems: [],
        });
      } else {
        level2Items.forEach((level2) => {
          const level3Items = findItems(level2.id, 3);

          if (level3Items.length === 0) {
            // level 1, 2가 있고 level 3이 없으면 → level 2가 최종
            finalItems.push({
              finalItem: level2,
              parentItems: [level1],
            });
          } else {
            // level 1, 2, 3이 모두 있으면 → level 3이 최종
            level3Items.forEach((level3) => {
              finalItems.push({
                finalItem: level3,
                parentItems: [level1, level2],
              });
            });
          }
        });
      }
    });

    return finalItems;
  }, [evalItemsData]);

  // 평가계획서 데이터 존재 여부 확인
  const hasEvalPlanData = React.useMemo(() => {
    return findFinalLevelItems.some(
      ({ finalItem }) =>
        (finalItem.extra_data?.elements &&
          finalItem.extra_data.elements.trim() !== "") ||
        (finalItem.extra_data?.criteria &&
          finalItem.extra_data.criteria.trim() !== ""),
    );
  }, [findFinalLevelItems]);

  const renderTableRows = () => {
    const level1Items = findItems(0, 1);
    return level1Items.flatMap((level1) => {
      const level2Items = findItems(level1.id, 2);
      if (level2Items.length === 0) {
        const rowKey = `level1-${level1.id}`;
        const finalItemData = findFinalLevelItems.find(
          ({ finalItem }) => finalItem.id === level1.id,
        );
        const elements = finalItemData?.finalItem?.extra_data?.elements || "";
        const criteria = finalItemData?.finalItem?.extra_data?.criteria || "";

        return (
          <tr key={rowKey}>
            <td style={{ ...getStickyColumnStyle(0) }}>
              {level1.content}
              {level1.extra_data?.weight && (
                <>
                  <br />
                  {t("teacher.lessonRelated.weight")}:{" "}
                  {level1.extra_data.weight}%
                </>
              )}
              {level1.extra_data?.total && !level2Items.length && (
                <>
                  <br />
                  {t("teacher.lessonRelated.totalScore")}:{" "}
                  {level1.extra_data.total}
                  <button
                    className="btn btn-sm btn-outline-secondary pt-0 pb-0 ps-2 pe-2"
                    onClick={() => handleAllClick(level1, 1)}
                  >
                    All
                  </button>
                </>
              )}
            </td>
            {findByLevels(2).length > 0 && (
              <td style={{ ...getStickyColumnStyle(1) }}></td>
            )}
            {findByLevels(3).length > 0 && (
              <td style={{ ...getStickyColumnStyle(2) }}></td>
            )}
            {showEvalPlan && hasEvalPlanData && (
              <td
                style={{
                  width: "150px",
                  minWidth: "100px",
                  maxWidth: "200px",
                  backgroundColor: "#f8f9fa",
                  fontSize: "11px",
                  padding: "4px",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {elements}
              </td>
            )}
            {showEvalPlan && hasEvalPlanData && (
              <td
                style={{
                  width: "150px",
                  minWidth: "100px",
                  maxWidth: "200px",
                  backgroundColor: "#f8f9fa",
                  fontSize: "11px",
                  padding: "4px",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {criteria}
              </td>
            )}
            {lessonStudentsData?.map((student) => {
              return renderTd(
                level1.evaluation_type,
                student.eval_item_grades,
                level1.id,
                student.username,
                student,
              );
            })}
          </tr>
        );
      } else {
        return level2Items.flatMap((level2, level2Index) => {
          const level3Items = findItems(level2.id, 3);
          if (level3Items.length === 0) {
            const rowKey = `level2-${level2.id}-${level2Index}`;
            const finalItemData = findFinalLevelItems.find(
              ({ finalItem }) => finalItem.id === level2.id,
            );
            const elements =
              finalItemData?.finalItem?.extra_data?.elements || "";
            const criteria =
              finalItemData?.finalItem?.extra_data?.criteria || "";

            return (
              <tr key={rowKey}>
                <td style={{ ...getStickyColumnStyle(0) }}>
                  {level2Index === 0 ? (
                    <>
                      {level1.content}
                      {level1.extra_data?.weight && (
                        <>
                          <br />
                          {t("teacher.lessonRelated.weight")}:{" "}
                          {level1.extra_data.weight}%
                        </>
                      )}
                      {level1.extra_data?.total && !level2Items.length && (
                        <>
                          <br />
                          {t("teacher.lessonRelated.totalScore")}:{" "}
                          {level1.extra_data.total}
                          <button
                            className="btn btn-sm btn-outline-secondary pt-0 pb-0 ps-2 pe-2"
                            onClick={() => handleAllClick(level1, 1)}
                          >
                            All
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    ""
                  )}
                </td>
                <td style={{ ...getStickyColumnStyle(1) }}>
                  {level2.content}
                  {level2.extra_data?.weight && (
                    <>
                      <br />
                      {t("teacher.lessonRelated.weight")}:{" "}
                      {level2.extra_data.weight}%
                    </>
                  )}
                  {level2.extra_data?.total && !level3Items.length && (
                    <>
                      <br />
                      {t("teacher.lessonRelated.totalScore")}:{" "}
                      {level2.extra_data.total}
                      <button
                        className="btn btn-sm btn-outline-secondary pt-0 pb-0 ps-2 pe-2"
                        onClick={() => handleAllClick(level2, 2)}
                      >
                        All
                      </button>
                    </>
                  )}
                </td>
                {findByLevels(3).length > 0 && (
                  <td style={{ ...getStickyColumnStyle(2) }}></td>
                )}
                {showEvalPlan && hasEvalPlanData && (
                  <td
                    style={{
                      width: "150px",
                      minWidth: "100px",
                      maxWidth: "200px",
                      backgroundColor: "#f8f9fa",
                      fontSize: "11px",
                      padding: "4px",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {elements}
                  </td>
                )}
                {showEvalPlan && hasEvalPlanData && (
                  <td
                    style={{
                      width: "150px",
                      minWidth: "100px",
                      maxWidth: "200px",
                      backgroundColor: "#f8f9fa",
                      fontSize: "11px",
                      padding: "4px",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {criteria}
                  </td>
                )}
                {lessonStudentsData?.map((student) => {
                  return renderTd(
                    level2.evaluation_type,
                    student.eval_item_grades,
                    level2.id,
                    student.username,
                    student,
                  );
                })}
              </tr>
            );
          } else {
            return level3Items.flatMap((level3, level3Index) => {
              const rowKey = `level3-${level3.id}-${level3Index}`;
              const finalItemData = findFinalLevelItems.find(
                ({ finalItem }) => finalItem.id === level3.id,
              );
              const elements =
                finalItemData?.finalItem?.extra_data?.elements || "";
              const criteria =
                finalItemData?.finalItem?.extra_data?.criteria || "";

              return (
                <tr key={rowKey}>
                  <td style={{ ...getStickyColumnStyle(0) }}>
                    {level2Index === 0 && level3Index === 0 ? (
                      <>
                        {level1.content}
                        {level1.extra_data?.weight && (
                          <>
                            <br />
                            {t("teacher.lessonRelated.weight")}:{" "}
                            {level1.extra_data.weight}%
                          </>
                        )}
                        {level1.extra_data?.total && !level2Items.length && (
                          <>
                            <br />
                            {t("teacher.lessonRelated.totalScore")}:{" "}
                            {level1.extra_data.total}
                            <button
                              className="btn btn-sm btn-outline-secondary pt-0 pb-0 ps-2 pe-2"
                              onClick={() => handleAllClick(level1, 1)}
                            >
                              All
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      ""
                    )}
                  </td>
                  <td style={{ ...getStickyColumnStyle(1) }}>
                    {level3Index === 0 ? (
                      <>
                        {level2.content}
                        {level2.extra_data?.weight && (
                          <>
                            <br />
                            {t("teacher.lessonRelated.weight")}:{" "}
                            {level2.extra_data.weight}%
                          </>
                        )}
                        {level2.extra_data?.total && !level3Items.length && (
                          <>
                            <br />
                            {t("teacher.lessonRelated.totalScore")}:{" "}
                            {level2.extra_data.total}
                            <button
                              className="btn btn-sm btn-outline-secondary pt-0 pb-0 ps-2 pe-2"
                              onClick={() => handleAllClick(level2, 2)}
                            >
                              All
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      ""
                    )}
                  </td>
                  <td style={{ ...getStickyColumnStyle(2) }}>
                    {level3.content}
                    {level3.extra_data?.weight && (
                      <>
                        <br />
                        {t("teacher.lessonRelated.weight")}:{" "}
                        {level3.extra_data.weight}%
                      </>
                    )}
                    {level3.extra_data?.total && (
                      <>
                        <br />
                        {t("teacher.lessonRelated.totalScore")}:{" "}
                        {level3.extra_data.total}
                        <button
                          className="btn btn-sm btn-outline-secondary pt-0 pb-0 ps-2 pe-2"
                          onClick={() => handleAllClick(level3, 3)}
                        >
                          All
                        </button>
                      </>
                    )}
                  </td>
                  {showEvalPlan && hasEvalPlanData && (
                    <td
                      style={{
                        width: "150px",
                        minWidth: "100px",
                        maxWidth: "200px",
                        backgroundColor: "#f8f9fa",
                        fontSize: "11px",
                        padding: "4px",
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {elements}
                    </td>
                  )}
                  {showEvalPlan && hasEvalPlanData && (
                    <td
                      style={{
                        width: "150px",
                        minWidth: "100px",
                        maxWidth: "200px",
                        backgroundColor: "#f8f9fa",
                        fontSize: "11px",
                        padding: "4px",
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {criteria}
                    </td>
                  )}
                  {lessonStudentsData?.map((student) => {
                    return renderTd(
                      level3.evaluation_type,
                      student.eval_item_grades,
                      level3.id,
                      student.username,
                      student,
                    );
                  })}
                </tr>
              );
            });
          }
        });
      }
    });
  };

  const handleAllClick = (item, level) => {
    const total = item.extra_data?.total;
    if (!total) return;

    setLessonStudentsData((prevRows) =>
      prevRows.map((student) => {
        if (student.lesson_id !== lessonInfo.id) return student;

        if (level === 1 || level === 2 || level === 3) {
          // shadow ref도 함께 갱신: 일괄 만점 적용도 사용자 입력의 일종.
          // (이 함수는 단일 setState 호출이라 자체 stale closure 위험은 없지만,
          // 이후 다른 셀 편집 → 저장 시 shadow 비교 로직이 정확하게 동작하도록
          // shadow에 반영해둔다.)
          if (!typedShadowRef.current[student.username]) {
            typedShadowRef.current[student.username] = {};
          }
          typedShadowRef.current[student.username][item.id] = String(total);

          const newGrades = {
            ...(student.eval_item_grades || {}),
            [item.id]: total,
          };
          const updatedStudent = {
            ...student,
            eval_item_grades: newGrades,
            isModified: true,
          };
          const grade = calculateGradeForStudent(updatedStudent);
          return {
            ...updatedStudent,
            grade,
            convert100Override: undefined,
            convert100: calculateConvert100(updatedStudent, grade),
          };
        }
        return student;
      }),
    );
    setHasUnsavedChanges(true);
  };

  // 저장 직전, 사용자가 타이핑한 값(shadow)과 실제 state의 eval_item_grades를
  // 비교한다. 값이 다르면 stale closure 등으로 입력이 유실됐을 가능성이 있으므로
  // 자동 복구한다.
  //
  // @param targetUsernames - 비교할 학생 username 배열. 비어있으면 shadow 전체.
  // @returns { mismatches: Array, recovered: boolean }
  //   - mismatches: [{ username, fullname, evalItemId, shadowVal, stateVal }]
  //   - recovered: 자동 복구를 위해 setLessonStudentsData를 호출했는지 여부
  const detectAndRecoverShadowMismatches = (targetUsernames) => {
    const shadow = typedShadowRef.current || {};
    const usernames =
      targetUsernames && targetUsernames.length > 0
        ? targetUsernames
        : Object.keys(shadow);

    const mismatches = [];

    for (const username of usernames) {
      const studentShadow = shadow[username];
      if (!studentShadow) continue;

      const student = lessonStudentsData.find(
        (d) => d.username === username && d.lesson_id === lessonInfo?.id,
      );
      if (!student) continue;

      const stateGrades = student.eval_item_grades || {};
      for (const evalItemId of Object.keys(studentShadow)) {
        const shadowRaw = studentShadow[evalItemId];
        const stateRaw = stateGrades[evalItemId];
        const sv = stateRaw == null ? "" : String(stateRaw);
        const tv = shadowRaw == null ? "" : String(shadowRaw);
        if (sv !== tv) {
          mismatches.push({
            username,
            fullname: student.fullname,
            studentId: student.id,
            evalItemId,
            shadowVal: tv,
            stateVal: sv,
          });
        }
      }
    }

    if (mismatches.length === 0) {
      return { mismatches, recovered: false };
    }

    // 진단용 로그: 운영 서버에서 발생 빈도/패턴 추적을 위해 기록
    console.warn(
      "[GRADE SHADOW MISMATCH] 저장 직전 state와 shadow가 불일치합니다. 자동 복구 시도.",
      {
        lessonId: lessonInfo?.id,
        count: mismatches.length,
        samples: mismatches.slice(0, 10),
      },
    );

    // shadow 값을 state에 다시 머지하고 isModified=true로 표시한다.
    setLessonStudentsData((prevRows) =>
      prevRows.map((row) => {
        if (row.lesson_id !== lessonInfo?.id) return row;
        const studentShadow = shadow[row.username];
        if (!studentShadow) return row;

        const prevGrades = row.eval_item_grades || {};
        const newGrades = { ...prevGrades };
        let changed = false;
        for (const [evalItemId, shadowRaw] of Object.entries(studentShadow)) {
          const prevRaw = prevGrades[evalItemId];
          const pv = prevRaw == null ? "" : String(prevRaw);
          const tv = shadowRaw == null ? "" : String(shadowRaw);
          if (pv !== tv) {
            newGrades[evalItemId] = shadowRaw;
            changed = true;
          }
        }
        if (!changed) return row;

        const updatedStudent = { ...row, eval_item_grades: newGrades };
        const newGrade = calculateGradeForStudent(updatedStudent);
        return {
          ...row,
          eval_item_grades: newGrades,
          grade: newGrade,
          convert100Override: undefined,
          convert100: calculateConvert100(updatedStudent, newGrade),
          isModified: true,
        };
      }),
    );

    return { mismatches, recovered: true };
  };

  //아... 드디어 save 만 하면 되는 것 같다...
  const saveStudentGrade = (student) => {
    // console.log("student", student);

    // 저장 직전, 해당 학생에 한해 shadow와 state를 비교/복구
    const { mismatches, recovered } = detectAndRecoverShadowMismatches([
      student.username,
    ]);
    if (recovered) {
      setTimeout(() => {
        alert(
          `입력값 ${mismatches.length}건이 화면 상태와 다르게 감지되어 자동 복구되었습니다.\n` +
            `값이 정상인지 확인 후 [저장] 버튼을 다시 눌러주세요.`,
        );
      }, 0);
      return;
    }

    if (!student.isModified) {
      // 변경사항이 없는 경우, 제출을 건너뜁니다.
      console.log("건너뛰기");
      handleToastCenterTop(t("common.messages.notModifed"));
      // setAlertModalState({
      //   visible: true,
      //   message: "테스트 해보자",
      // });
      return;
    }

    // 100점제는 저장 직전 숫자 형식 재검증.
    // 유효하지 않은 값이 하나라도 있으면 저장을 중단한다.
    const invalidEntries = findInvalidEvalItemGrades([student]);
    if (invalidEntries.length > 0) {
      const first = invalidEntries[0];
      alert(
        `숫자 형식이 아닌 입력이 있어 저장할 수 없습니다.\n` +
          `학생: ${first.fullname || first.username}\n` +
          `항목: ${first.evalItemName}\n` +
          `입력값: ${first.value ?? ""}\n\n` +
          `숫자(소수점/음수 포함)로 수정 후 다시 저장해주세요.`,
      );
      return;
    }

    const { id, comment, eval_item_grades, tags, extra_data } = student;
    let grade = calculateGradeForStudent(student);
    if (isGradeError(grade) && student.gradeOverride != null) {
      grade = student.gradeOverride;
    }

    const dataSet = { id, comment, eval_item_grades, tags, extra_data, grade };

    // convert100 데이터가 있을 때만 추가
    const convert100Setting = selectedConvert100Setting;
    if (convert100Setting) {
      dataSet.convert100 = calculateConvert100(student, grade);
      if (shouldUseConvert100Override(student, dataSet.convert100)) {
        dataSet.convert100 = student.convert100Override;
      }
    }

    if (!hasUpdatePermission()) {
      handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
      return;
    }

    // 글자수 검증 - 세부특기사항 (comment)
    if (
      !validateTextLength(
        dataSet,
        "comment",
        textLengthSettings,
        "subjectComment",
        handleToastCenterBottom,
        student?.fullname + "의 세부특기사항" || "",
      )
    ) {
      return;
    }

    // 글자수 검증 - 생기부용코멘트 (extra_data.schoolLifeComment)
    if (
      extra_data?.schoolLifeComment &&
      !validateTextLength(
        { schoolLifeComment: extra_data.schoolLifeComment },
        "schoolLifeComment",
        textLengthSettings,
        "schoolLifeSubjectComment",
        handleToastCenterBottom,
        student?.fullname + "의 생기부용코멘트" || "",
      )
    ) {
      return;
    }

    // const checkOwner = lessonInfo.teachers.some(
    //   (teacher) => teacher.username === user.username
    // );
    // if (!checkOwner && !hasPermission(null, "can_update")) {
    //   handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
    //   return;
    // }

    setIsLoadingModal(true);
    updateLessonStudents.mutate(dataSet, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setSavedRows((prev) => [...prev, dataSet.id]);
        setTimeout(() => {
          setSavedRows([]);
        }, 1000);
        // 저장 후 남은 변경사항 체크
        const hasRemainingChanges = lessonStudentsData.some(
          (d) => d.id !== id && d.isModified,
        );
        if (!hasRemainingChanges) {
          setHasUnsavedChanges(false);
        }
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
    // 성공적으로 제출 후, 해당 데이터 세트의 isModified 상태를 초기화합니다.
    setLessonStudentsData((currentDataSets) =>
      currentDataSets.map((dataSet) =>
        dataSet.id === id ? { ...dataSet, isModified: false } : dataSet,
      ),
    );
  };

  const saveAllStudentsGrade = () => {
    // 저장 직전, 사용자가 타이핑한 값(shadow)과 state가 일치하는지 확인.
    // 불일치하면 자동 복구 후 사용자에게 다시 저장을 요청한다.
    // shadow 전체를 검사 (어떤 학생의 isModified 플래그 자체가 누락되었을 수도
    // 있으므로 modified 학생으로 한정하지 않는다).
    const { mismatches, recovered } = detectAndRecoverShadowMismatches();
    if (recovered) {
      const sampleNames = mismatches
        .map((m) => m.fullname || m.username)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .slice(0, 5)
        .join(", ");
      // 복구된 값으로 화면이 먼저 다시 그려진 후 alert가 뜨도록 다음 tick으로 미룸
      setTimeout(() => {
        alert(
          `입력값 ${mismatches.length}건이 화면 상태와 다르게 감지되어 자동 복구되었습니다.\n` +
            `값이 정상인지 확인 후 [전체저장] 버튼을 다시 눌러주세요.\n\n` +
            `대상 학생: ${sampleNames}${
              mismatches.length > 5 ? " 외" : ""
            }`,
        );
      }, 0);
      return;
    }

    const modifiedStudents = lessonStudentsData.filter(
      (data) => data.isModified === true,
    );

    // 100점제는 저장 직전 숫자 형식 재검증.
    // 유효하지 않은 값이 있으면 전체저장을 중단한다.
    const invalidEntries = findInvalidEvalItemGrades(modifiedStudents);
    if (invalidEntries.length > 0) {
      const uniqTargets = [
        ...new Set(
          invalidEntries.map(
            (item) => `${item.fullname || item.username} / ${item.evalItemName}`,
          ),
        ),
      ];
      const preview = uniqTargets.slice(0, 5).join("\n");
      alert(
        `숫자 형식이 아닌 입력 ${invalidEntries.length}건이 있어 전체저장을 중단했습니다.\n\n` +
          `${preview}${uniqTargets.length > 5 ? "\n..." : ""}\n\n` +
          `숫자(소수점/음수 포함)로 수정 후 다시 저장해주세요.`,
      );
      return;
    }

    const dataArray = modifiedStudents
      .map((student) => {
        const { id, comment, eval_item_grades, tags, extra_data } = student;

        let grade = calculateGradeForStudent(student);
        if (isGradeError(grade) && student.gradeOverride != null) {
          grade = student.gradeOverride;
        }

        const dataSet = {
          id,
          comment,
          eval_item_grades,
          tags,
          extra_data,
          grade,
        };

        // convert100 데이터가 있을 때만 추가
        const convert100Setting = selectedConvert100Setting;
        if (convert100Setting) {
          dataSet.convert100 = calculateConvert100(student, grade);
          if (shouldUseConvert100Override(student, dataSet.convert100)) {
            dataSet.convert100 = student.convert100Override;
          }
        }

        return dataSet;
      });

    if (dataArray.length < 1) {
      // 변경사항이 없는 경우, 제출을 건너뜁니다.
      console.log("건너뛰기");
      handleToastCenterTop(t("common.messages.notModifed"));
      return;
    }

    if (!hasUpdatePermission()) {
      handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
      return;
    }

    // 글자수 검증 - 세부특기사항 (배열 전체)
    if (
      !validateTextLength(
        dataArray,
        "comment",
        textLengthSettings,
        "subjectComment",
        handleToastCenterBottom,
        "세부특기사항",
      )
    ) {
      return;
    }

    // 글자수 검증 - 생기부용코멘트 (배열 전체)
    // extra_data.schoolLifeComment가 있는 항목들만 검증
    const itemsWithSchoolLifeComment = dataArray.filter(
      (item) => item.extra_data?.schoolLifeComment,
    );
    if (itemsWithSchoolLifeComment.length > 0) {
      const schoolLifeCommentArray = itemsWithSchoolLifeComment.map((item) => ({
        schoolLifeComment: item.extra_data.schoolLifeComment,
      }));
      if (
        !validateTextLength(
          schoolLifeCommentArray,
          "schoolLifeComment",
          textLengthSettings,
          "schoolLifeSubjectComment",
          handleToastCenterBottom,
          "생기부용코멘트",
        )
      ) {
        return;
      }
    }

    // const checkOwner = lessonInfo.teachers.some(
    //   (teacher) => teacher.username === user.username
    // );
    // if (!checkOwner && !hasPermission(null, "can_update")) {
    //   handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
    //   return;
    // }

    // 변경된 데이터 세트 처리 로직...
    // console.log("제출된 데이터 세트:", dataArray);
    setIsLoadingModal(true);
    updateLessonStudentsBulk.mutate(dataArray, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setSavedBulk(true);
        setTimeout(() => {
          setSavedBulk(false);
        }, 1000);

        // 성공적으로 제출 후, 해당 데이터 세트의 isModified 상태를 초기화합니다.
        setLessonStudentsData((currentDataSets) =>
          currentDataSets.map((data) =>
            data.isModified === true ? { ...data, isModified: false } : data,
          ),
        );
        setHasUnsavedChanges(false);
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  const handleDelete = (row) => {
    const id = row.id;

    if (!hasDeletePermission()) {
      handleToastCenterTop(t("common.messages.ownerDeletePermission"));
      return;
    }
    // const checkOwner = lessonInfo.teachers.some(
    //   (teacher) => teacher.username === user.username
    // );
    // if (!checkOwner && !hasPermission(null, "can_update")) {
    //   handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
    //   return;
    // }

    setIsLoadingModal(true);
    deleteLessonStudents.mutate(id, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setConfirmDelete([]);
        setLessonStudentsData((rows) => rows.filter((row) => row.id !== id));
      },
      onError: (error) => {
        setIsLoadingModal(false);
        setConfirmDelete([]);
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  const handleCancelDelete = () => {
    setConfirmDelete([]);
  };

  const handleConfirmDelete = (row) => {
    setConfirmDelete((prev) => [...prev, row.id]);
  };

  /* 완전 복잡하군... 찬찬히 하나씩 적어보자. 
이 함수는 3단계 계층 구조(Level 1 > Level 2 > Level 3)의 평가 항목에 대해 다음과 같은 특징을 가진 점수 계산을 수행합니다:
각 레벨은 가중치를 가질 수 있음
빈 항목 제외 옵션 (excludeEmptyGrade)
두 가지 계산 방식:
maxAverage: 각 항목의 퍼센티지 평균
기본: (총점/총만점) * 100
가중치가 있는 경우와 없는 경우를 구분하여 처리
각 레벨의 가중치 합이 100인지 검증


flexibleData에서 covert100Setting 정보를 가져와 아래 2개의 유무를 결정한다.
calculType: 
  - maxAverage 그레이스기독학교의 해당 단계의 만점의 점수를 퍼센티지로 환산한 것이 점수이고,
그 점수를 해당 카테고리안에서 모두 더한 후 평균을 내는 방식이다.
  - 이거 없으면, 헤이븐이 사용하는 방식으로 한 카데고리 내에서 모든 입력 점수 나누기 모든 만점이다.

excludeEmptyGrade
 - true면, 헤이븐 학교에서 점수를 입력하지 않은 항목은, 만점을 모두 더하는 것에서 제외 된다.
 - false면, 입력 안 한 항목이 0점과 동일한 효과가 된다.

 - true이면서 maxAverage일 때, 평균 점수 낼 때 제외되도록 추가 개발해야 한다. 
*/

  // 감점 방식 계산을 위한 헬퍼 함수
  const isEmptyGrade = (gradeValue) => {
    return gradeValue === "" || gradeValue === null || gradeValue === undefined;
  };

  // Level 2/3 항목의 감점 계산
  const processLevel2Deduction = (
    level2Items,
    student,
    evalItemsData,
    maxPoints,
    excludeEmptyGrade,
  ) => {
    // Level 2 항목들의 총 만점 계산
    let level2TotalMax = 0;
    let earnedScore = 0;

    level2Items.forEach((level2Item) => {
      // Level 3 확인
      const level3Items = evalItemsData.filter(
        (i) => i.hierarchy_level === 3 && i.parent_id === level2Item.id,
      );

      if (level3Items.length > 0) {
        // Level 3가 있으면 Level 3의 총 만점과 획득 점수
        level3Items.forEach((level3Item) => {
          const score = student.eval_item_grades?.[level3Item.id];
          const total = Number(level3Item.extra_data?.total || 100);

          if (!excludeEmptyGrade || !isEmptyGrade(score)) {
            level2TotalMax += total;
            earnedScore += Number(score || 0);
          }
        });
      } else {
        // Level 2 직접 평가
        const score = student.eval_item_grades?.[level2Item.id];
        const total = Number(level2Item.extra_data?.total || 100);

        if (!excludeEmptyGrade || !isEmptyGrade(score)) {
          level2TotalMax += total;
          earnedScore += Number(score || 0);
        }
      }
    });

    // 감점 계산
    if (level2TotalMax === 0) return 0;
    const earnedPoints = (earnedScore / level2TotalMax) * maxPoints;
    const deduction = maxPoints - earnedPoints;
    return deduction;
  };

  // 감점 방식으로 총점 계산 (havenStyle = true)
  const calculateDeductionScore = (
    student,
    evalItemsData,
    excludeEmptyGrade,
  ) => {
    let totalDeduction = 0; // 총 감점

    // Level 1 항목들 순회
    const level1Items = evalItemsData.filter(
      (item) => item.hierarchy_level === 1,
    );

    if (level1Items.length === 0) return "";

    level1Items.forEach((level1Item) => {
      const level1Weight = Number(level1Item.extra_data?.weight || 0);
      const maxPoints = level1Weight; // 가중치 = 최대 획득 가능 점수

      // Level 2가 있는지 확인
      const level2Items = evalItemsData.filter(
        (item) =>
          item.hierarchy_level === 2 && item.parent_id === level1Item.id,
      );

      if (level2Items.length === 0) {
        // Level 1 직접 평가
        const score = student.eval_item_grades?.[level1Item.id];
        const total = level1Item.extra_data?.total;

        if (excludeEmptyGrade && isEmptyGrade(score)) return;

        if (score && total) {
          const earnedPoints = (Number(score) / Number(total)) * maxPoints;
          const deduction = maxPoints - earnedPoints;
          totalDeduction += deduction;
        } else if (!excludeEmptyGrade && total) {
          // 빈 칸인데 excludeEmptyGrade가 false면 만점 전체 감점
          totalDeduction += maxPoints;
        }
      } else {
        // Level 2 처리
        const deduction = processLevel2Deduction(
          level2Items,
          student,
          evalItemsData,
          maxPoints,
          excludeEmptyGrade,
        );
        totalDeduction += deduction;
      }
    });

    const finalScore = 100 - totalDeduction;
    return Number(finalScore.toFixed(2)).toString();
  };

  function calculateTotalScore(student, evalItemsData) {
    // 설정 가져오기: 평가항목별 설정에서 계산 방식과 빈 항목 제외 여부 확인
    const covert100Setting = flexibleData?.find(
      (r) =>
        r.sub_category === "평가항목별" && r.main_category === "convert100",
    )?.data_value;
    const calculType = covert100Setting?.calculType || ""; // maxAverage or 기본
    const excludeEmptyGrade = covert100Setting?.excludeEmptyGrade || false; // 빈 항목 제외 여부
    const havenStyle = covert100Setting?.havenStyle || false; // 감점 방식 계산 여부

    const isTotalScore = lessonSetting
      ?.find((item) => item.item_code === "evalItem")
      ?.detailed_setting?.includes("만점");

    // 기본 유효성 검사: 필수 데이터 확인
    if (!student || !evalItemsData || !Array.isArray(evalItemsData)) {
      console.warn("Invalid input parameters for calculateTotalScore");
      return 0;
    }

    // 이수제 평가인 경우 빈 문자열 반환
    if (evalItemsData.some((item) => item.evaluation_type === "이수제")) {
      return "";
    }

    // 평가 점수가 없는 경우 빈 문자열 반환
    if (!student.eval_item_grades) return "";

    if (shouldUseTotalOnlyScore()) {
      return calculateTotalOnlyScore(
        student,
        evalItemsData,
        calculType,
        excludeEmptyGrade,
      );
    }

    // havenStyle이 true이고 100점제일 때만 감점 방식 적용
    if (havenStyle && checkEvalTypes(["100점제"])) {
      return calculateDeductionScore(student, evalItemsData, excludeEmptyGrade);
    }

    // Level 1 항목들만 필터링
    const level1Items = evalItemsData.filter(
      (item) => item.hierarchy_level === 1,
    );
    if (level1Items.length === 0) return "";

    // 빈 값 체크 헬퍼 함수
    const isEmptyGrade = (gradeValue) => {
      return (
        gradeValue === "" || gradeValue === null || gradeValue === undefined
      );
    };

    // excludeEmptyGrade가 true일 때, 모든 점수가 빈칸인지 확인
    if (excludeEmptyGrade) {
      const allGradesEmpty = Object.values(
        student.eval_item_grades || {},
      ).every((value) => isEmptyGrade(value));
      if (allGradesEmpty) {
        return "";
      }
    }

    let finalScore = 0; // 최종 점수
    let totalWeights = 0; // Level 1 가중치 합계
    let warningNoTotalScores = []; // 만점이 없는 항목 경고 메시지 저장

    // 여기 위까지 기본 설정 했음------------------------

    // Level 1 항목들 순회
    level1Items.forEach((level1Item) => {
      // console.log("level1Item", level1Item);

      if (!level1Item?.id) return;

      // level1Item의 하위 항목인 Level 2 항목들 찾기
      const level2Items = evalItemsData.filter(
        (item) =>
          item.hierarchy_level === 2 && item.parent_id === level1Item.id,
      );

      // === Level 1 직접 평가 케이스 (하위 항목이 없는 경우) ===

      const level1Weight = Number(level1Item.extra_data?.weight || 0);
      if (!isNaN(level1Weight)) totalWeights += level1Weight;

      if (level2Items.length === 0) {
        //1단계의 점수
        const directScore = student.eval_item_grades?.[level1Item.id];
        //1단계의 만점
        const directTotal = level1Item.extra_data?.total;

        // 빈 항목 제외 설정이 true이고 점수가 비어있으면 건너뛰기
        //빈 항목 건너뛰기는 가중치가 있을 때는 작동할 수 없고, 1단계는 가중치가 필수이기 때문에 여기선 필요 없다.
        // if (excludeEmptyGrade && isEmptyGrade(directScore)) {
        //   return;
        // }

        // 원점수제에 작동하는 것이기에 1단계에 가중치가 없으면 작동할 수 없다.

        // 만점이 있을 경우 점수 계산: (획득점수/만점) * 100 * 가중치
        if (directScore && directTotal) {
          const percentage = (Number(directScore) / Number(directTotal)) * 100;
          finalScore +=
            Math.round(((percentage * level1Weight) / 100) * 100) / 100;
          return;
        }

        //만점이 없을 경우 점수 계산: 획득점수 * 가중치
        if (directScore) {
          if (directTotal === 0) {
            warningNoTotalScores.push({
              content: level1Item.content,
              level: level1Item.hierarchy_level,
            });
          }

          finalScore +=
            Math.round(((Number(directScore) * level1Weight) / 100) * 100) /
            100;
          return;
        }
      }

      // === Level 2 처리 ===

      //이 아래는 그냥 2단계 가중치가 100인지 경고 메시지 날려주는 곳이다. -------------
      // Level 2 가중치 합계 검증
      let level2TotalWeights = 0;
      let hasLevel2Weights = false;

      // Level 2 가중치 확인
      level2Items.forEach((item) => {
        const weight = Number(item.extra_data?.weight || 0);
        if (!isNaN(weight) && weight > 0) {
          hasLevel2Weights = true;
          level2TotalWeights += weight;
        }
      });

      // Level 2 가중치 합이 100이 아닌 경우 경고
      // 부동소수점 오차(예: 8.3 * 11 + 8.7 = 99.99999999999999)를 허용하기 위해 epsilon 비교 사용
      if (
        hasLevel2Weights &&
        Math.abs(level2TotalWeights - 100) > 0.0001 &&
        !window.level2WeightWarningShown &&
        !evalItemsData.some((item) => item.evaluation_type === "이수제")
      ) {
        const displayLevel2Total =
          Math.round(level2TotalWeights * 10000) / 10000;
        console.warn(
          `Level 2 total weights (${displayLevel2Total}) does not equal 100%`,
        );
        alert(t("teacher.lessonRelated.level2WeightSumMessage"));
        window.level2WeightWarningShown = true;
        setTimeout(() => {
          window.level2WeightWarningShown = false;
        }, 3000);
      }

      //이 위는 그냥 2단계 가중치가 100인지 경고 메시지 날려주는 곳이다. -------------

      let level1WeightedScore = 0; // Level 1의 가중 점수

      // excludeEmptyGrade가 true면 빈칸은 합산하지 않는다.
      const nonEmptyLevel2Items = excludeEmptyGrade
        ? level2Items.filter(
            (item) => !isEmptyGrade(student.eval_item_grades?.[item.id]),
          )
        : level2Items;

      // 모든 Level 2 항목이 비어있으면 이 Level 1은 건너뛰기, 하지만 이렇게 하면 3단계로도 못 가잖아? 그래서 안 되었던 거군.
      // if (excludeEmptyGrade && nonEmptyLevel2Items.length === 0) {
      //   return;
      // }

      // Level 2 항목들 처리
      level2Items.forEach((level2Item) => {
        // Level 3 항목 찾기
        const level3Items = evalItemsData.filter(
          (item) =>
            item.hierarchy_level === 3 && item.parent_id === level2Item.id,
        );

        const level2Weight = Number(level2Item.extra_data?.weight || 0);

        // === Level 2 직접 평가 케이스 (Level 3이 없는 경우) ===
        if (level3Items.length === 0) {
          const score = student.eval_item_grades?.[level2Item.id] || 0;

          //만점이 없지만 100점이면 기본 100점이 만점으로 들어가도록 한다.
          const total =
            level2Item.extra_data?.total || (isTotalScore ? 100 : 100);

          if (
            (!level2Item.extra_data?.total ||
              Number(level2Item.extra_data.total) <= 0) &&
            isTotalScore
          ) {
            warningNoTotalScores.push({
              content: level2Item.content,
              level: level2Item.hierarchy_level,
            });
          }

          // 빈 항목 제외 설정이 true이고 점수가 비어있으면 건너뛰기
          if (excludeEmptyGrade && isEmptyGrade(score)) {
            return;
          }

          if (score && total) {
            const percentage = (Number(score) / Number(total)) * 100;

            if (hasLevel2Weights) {
              // 가중치와 만점이 있는 경우: 퍼센티지 * 가중치가 1단계 가중치 적용된 점수가 된다.
              // 예를 들어서 2단계의 score 50, total 100, 가중치 50이면, 50/100 * 50 = 25이고, 이게 1단계 가중치 적용된 점수가 된다.
              level1WeightedScore += (percentage * level2Weight) / 100;
            } else {
              if (calculType === "maxAverage") {
                // 아무도 안 쓴느 방식. 그레이스는 3단계로 하니까. 이건 2단계에서 가중치 없이 만점만 있을 때 이렇게 하고 싶다고 하면 할 수 있는 방식 평균 방식: 각 항목의 퍼센티지 평균
                level1WeightedScore +=
                  percentage /
                  (excludeEmptyGrade
                    ? nonEmptyLevel2Items.length
                    : level2Items.length);
              } else {
                // 헤이븐 방식. 2단계에 가중치가 없고 만점만 있는 경우: (총점/총만점) * 100

                //입력된 모든 점수 합하기
                const totalScore = nonEmptyLevel2Items.reduce((sum, item) => {
                  const score = Number(
                    student.eval_item_grades?.[item.id] || 0,
                  );
                  return isNaN(score) ? sum : sum + score;
                }, 0);

                //만점 점수 모두 합하기 (빈칸 합산 설정 했으면 빈칸은 합산하지 않는다.)
                const totalPossible = nonEmptyLevel2Items.reduce(
                  (sum, item) => {
                    const total = Number(item.extra_data?.total || 100);
                    return isNaN(total) ? sum : sum + total;
                  },
                  0,
                );

                if (totalPossible > 0) {
                  const percentage = (totalScore / totalPossible) * 100;
                  level1WeightedScore = percentage;
                }
              }
            }
          }
          return;
        }

        // 이 아래는 3단계 처리. 그레이스 학교 중점  -------------------------------------

        // Level 3 처리
        let level2WeightedScore = 0;

        //이 아래는 그냥 3단계 가중치가 100인지 경고 메시지 날려주는 곳이다. -------------
        // Level 3 가중치 합계 검증
        let level3TotalWeights = 0;
        let hasLevel3Weights = false;

        level3Items.forEach((item) => {
          const weight = Number(item.extra_data?.weight || 0);
          if (!isNaN(weight) && weight > 0) {
            hasLevel3Weights = true;
            level3TotalWeights += weight;
          }
        });

        if (
          hasLevel3Weights && // 가중치가 있는 경우에만 검증
          Math.abs(level3TotalWeights - 100) > 0.0001 &&
          !window.level3WeightWarningShown &&
          !evalItemsData.some((item) => item.evaluation_type === "이수제")
        ) {
          const displayLevel3Total =
            Math.round(level3TotalWeights * 10000) / 10000;
          console.warn(
            `Level 3 total weights (${displayLevel3Total}) does not equal 100%`,
          );
          alert(
            t("teacher.lessonRelated.level3WeightSumMessage") ||
              "가중치 합계는 100이어야 합니다. (3단계)",
          );
          window.level3WeightWarningShown = true;
          setTimeout(() => {
            window.level3WeightWarningShown = false;
          }, 3000);
        }

        //이 위는 그냥 3단계 가중치가 100인지 경고 메시지 날려주는 곳이다. -------------

        // 비어있지 않은 level3 항목만 필터링
        const nonEmptyLevel3Items = excludeEmptyGrade
          ? level3Items.filter(
              (item) => !isEmptyGrade(student.eval_item_grades?.[item.id]),
            )
          : level3Items;

        if (excludeEmptyGrade && nonEmptyLevel3Items.length === 0) {
          return; // 모든 level3 항목이 비어있으면 이 level2는 건너뜀
          //그럼 해당 level2는 0점으로 처리된다.
        }

        //그레이스 기독학교 방식
        if (calculType === "maxAverage") {
          if (hasLevel3Weights) {
            // 가중치와 만점이 있는 경우. 만점이 없으면 안되네. 가중치가 있으면 maxAverage 방식도 적용 안 된다.
            level2WeightedScore = nonEmptyLevel3Items.reduce((sum, item) => {
              if (
                (!item.extra_data?.total ||
                  Number(item.extra_data.total) <= 0) &&
                isTotalScore
              ) {
                warningNoTotalScores.push({
                  content: item.content,
                  level: item.hierarchy_level,
                });
              }

              const score = Number(student.eval_item_grades?.[item.id] || 0);
              const total = Number(item.extra_data?.total || 100);
              const weight = Number(item.extra_data?.weight || 0);
              const percentage = total > 0 ? (score / total) * 100 : 0;
              return sum + (percentage * weight) / 100;
            }, 0);
          } else {
            // 각 Level 3 항목의 개별 퍼센티지 계산 후 평균 - 이게 maxAverage 방식이다.
            const percentages = nonEmptyLevel3Items.map((item) => {
              if (
                (!item.extra_data?.total ||
                  Number(item.extra_data.total) <= 0) &&
                isTotalScore
              ) {
                warningNoTotalScores.push({
                  content: item.content,
                  level: item.hierarchy_level,
                });
              }

              const score = Number(student.eval_item_grades?.[item.id] || 0);
              const total = Number(item.extra_data?.total || 100);
              return total > 0 ? (score / total) * 100 : 0;
            });

            if (percentages.length > 0) {
              level2WeightedScore =
                percentages.reduce((sum, p) => sum + p, 0) / percentages.length;
            }
          }
        } else {
          if (hasLevel3Weights) {
            // 가중치가 있는 경우
            level2WeightedScore = nonEmptyLevel3Items.reduce((sum, item) => {
              if (
                (!item.extra_data?.total ||
                  Number(item.extra_data.total) <= 0) &&
                isTotalScore
              ) {
                warningNoTotalScores.push({
                  content: item.content,
                  level: item.hierarchy_level,
                });
              }

              const score = Number(student.eval_item_grades?.[item.id] || 0);
              const total = Number(item.extra_data?.total || 100);
              const weight = Number(item.extra_data?.weight || 0);
              const percentage = total > 0 ? (score / total) * 100 : 0;
              return sum + (percentage * weight) / 100;
            }, 0);
          } else {
            // Level 3 만점 방식 처리
            const totalScore = nonEmptyLevel3Items.reduce((sum, item) => {
              const score = Number(student.eval_item_grades?.[item.id] || 0);
              return isNaN(score) ? sum : sum + score;
            }, 0);

            const totalPossible = nonEmptyLevel3Items.reduce((sum, item) => {
              if (
                (!item.extra_data?.total ||
                  Number(item.extra_data.total) <= 0) &&
                isTotalScore
              ) {
                warningNoTotalScores.push({
                  content: item.content,
                  level: item.hierarchy_level,
                });
              }

              const total = Number(
                item.extra_data?.total || (isTotalScore ? 100 : 100),
              );

              return isNaN(total) ? sum : sum + total;
            }, 0);

            if (totalPossible > 0) {
              const percentage = (totalScore / totalPossible) * 100;
              level2WeightedScore = percentage;
            }
          }
        }
        level1WeightedScore += (level2WeightedScore * level2Weight) / 100;
      });

      finalScore +=
        Math.round(((level1WeightedScore * level1Weight) / 100) * 100) / 100;
    });

    // Level 1 가중치 합계 검증
    let hasLevel1Weights = false;
    level1Items.forEach((item) => {
      const weight = Number(item.extra_data?.weight || 0);
      if (!isNaN(weight) && weight > 0) {
        hasLevel1Weights = true;
      }
    });

    if (
      hasLevel1Weights && // 가중치가 있는 경우에만 검증
      Math.abs(totalWeights - 100) > 0.0001 &&
      !window.level1WeightWarningShown &&
      !evalItemsData.some((item) => item.evaluation_type === "이수제")
    ) {
      const displayLevel1Total = Math.round(totalWeights * 10000) / 10000;
      console.warn(
        `Level 1 total weights (${displayLevel1Total}) does not equal 100%`,
      );
      alert(t("teacher.lessonRelated.level1WeightSumMessage"));
      window.level1WeightWarningShown = true;
      setTimeout(() => {
        window.level1WeightWarningShown = false;
      }, 3000);
    }
    // const score = Math.min(Math.max(finalScore, 0), 100);
    if (warningNoTotalScores.length > 0 && checkEvalTypes(["100점제"])) {
      handleToastCenterTop(
        t("teacher.lessonRelated.warningNoTotalScoresMessage") +
          "<br />" +
          warningNoTotalScores
            .map((item) => item.level + "단계의 " + item.content + "<br />")
            .join(""),
        5000,
      );
    }
    const score = finalScore;
    return Number(score.toFixed(2)).toString();
  }

  // 열 너비를 저장할 state 추가
  const [columnWidths, setColumnWidths] = useState({
    col1: 110,
    col2: 110,
    col3: 120,
  });

  // 참조를 위한 refs 설정
  const col1Ref = useRef(null);
  const col2Ref = useRef(null);
  const col3Ref = useRef(null);

  // 컴포넌트 마운트 및 리사이즈 시 열 너비 계산
  useEffect(() => {
    const calculateColumnWidths = () => {
      const widths = {
        col1: col1Ref.current ? col1Ref.current.offsetWidth : 110,
        col2: col2Ref.current ? col2Ref.current.offsetWidth : 110,
        col3: col3Ref.current ? col3Ref.current.offsetWidth : 120,
      };

      setColumnWidths(widths);
    };

    // 초기 계산
    calculateColumnWidths();

    // 리사이즈 이벤트에 대한 리스너 추가
    window.addEventListener("resize", calculateColumnWidths);

    return () => {
      window.removeEventListener("resize", calculateColumnWidths);
    };
  }, [evalItemsData]); // 평가 항목이 변경될 때 다시 계산

  // 동적 위치 계산 함수
  const getStickyColumnStyle = (columnIndex) => {
    // 기본 스타일: 모든 위치에 공통 스타일
    const baseStyle = {
      backgroundColor: "#fff",
      boxShadow:
        "inset 0 -1px 0 #dee2e6, inset 0 1px 0 #dee2e6, inset -1px 0 0 #dee2e6, inset 1px 0 0 #dee2e6, 2px 0 5px -2px rgba(0,0,0,0.2)",
    };
    if (columnIndex === 0) {
      return {
        position: "sticky",
        left: 0,
        zIndex: 3,
        ...baseStyle,
      };
    } else if (columnIndex === 1) {
      return {
        position: "sticky",
        left: `${columnWidths.col1}px`,
        zIndex: 2,
        ...baseStyle,
      };
    } else if (columnIndex === 2) {
      return {
        position: "sticky",
        left:
          findByLevels(2).length > 0
            ? `${columnWidths.col1 + columnWidths.col2}px`
            : `${columnWidths.col1}px`,
        zIndex: 1,
        ...baseStyle,
      };
    }
    return {};
  };

  return (
    <div>
      <div className="ms-3" style={{ minWidth: "800px" }}>
        <table
          className="table table-sm table-bordered"
          style={{
            fontSize: "13px",
            width: "auto",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 4,
              }}
            >
              <th
                className="align-middle"
                style={{
                  minWidth: "110px",
                  maxWidth: "250px",
                  ...getStickyColumnStyle(0),
                }}
              >
                {t("teacher.lessonRelated.level1")}

                {hasUpdatePermission() && (
                  <button
                    className="btn btn-sm btn-primary ps-1 pe-1 ms-1"
                    style={{ fontSize: "11px" }}
                    onClick={() => saveAllStudentsGrade()}
                  >
                    {savedBulk
                      ? t("common.labels.savedWell")
                      : t("common.labels.saveAll")}
                  </button>
                )}
              </th>
              {findByLevels(2).length > 0 && (
                <th
                  className="align-middle"
                  style={{
                    minWidth: "110px",
                    maxWidth: "250px",
                    ...getStickyColumnStyle(1),
                  }}
                >
                  {t("teacher.lessonRelated.level2")}
                </th>
              )}
              {findByLevels(3).length > 0 && (
                <th
                  className="align-middle"
                  style={{
                    minWidth: "120px",
                    maxWidth: "250px",
                    ...getStickyColumnStyle(2),
                  }}
                >
                  {t("teacher.lessonRelated.level3")}
                </th>
              )}
              {showEvalPlan && hasEvalPlanData && (
                <th
                  className="text-center align-middle"
                  style={{
                    width: "150px",
                    minWidth: "100px",
                    maxWidth: "200px",
                    fontSize: "11px",
                    backgroundColor: "#f8f9fa",
                    padding: "4px",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {t("teacher.lessonRelated.evalElements") || "평가요소"}
                </th>
              )}
              {showEvalPlan && hasEvalPlanData && (
                <th
                  className="text-center align-middle"
                  style={{
                    width: "150px",
                    minWidth: "100px",
                    maxWidth: "200px",
                    fontSize: "11px",
                    backgroundColor: "#f8f9fa",
                    padding: "4px",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {t("teacher.lessonRelated.evalCriteria") || "평가기준"}
                </th>
              )}
              {lessonStudentsData.map((student) => (
                <th
                  key={`student-${student.id}`}
                  className="text-center"
                  style={{
                    minWidth: "50px",
                    width: "100px",
                    maxWidth: "150px",
                    fontSize: "11px",
                    // verticalAlign: "top",
                  }}
                >
                  {student.fullname}
                  <br />
                  {hasUpdatePermission() && (
                    <button
                      className="btn btn-sm btn-primary ps-2 pe-2 p-0 m-0"
                      style={{ fontSize: "11px" }}
                      onClick={() => saveStudentGrade(student)}
                    >
                      {savedRows.includes(student.id)
                        ? t("common.labels.complete") || "완료"
                        : t("common.labels.save") || "저장"}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderTableRows()}</tbody>
          <tfoot>
            {checkEvalTypes(["100점제"]) &&
            lessonSetting.find((r) => r.item_code === "evalArea100")
              .saved_setting === "원점수기록" ? (
              <tr>
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(0) }}
                >
                  합계
                </th>
                {findByLevels(2).length > 0 && (
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(1) }}
                  ></th>
                )}
                {findByLevels(3).length > 0 && (
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(2) }}
                  ></th>
                )}
                {showEvalPlan && hasEvalPlanData && <th></th>}
                {showEvalPlan && hasEvalPlanData && <th></th>}

                {lessonStudentsData.map((student) => (
                  <th
                    key={`sum-${student.id}`}
                    className="text-center"
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    {student?.grade || ""}
                    {/* {student.eval_item_grades
                      ? calculateTotalScore(student, evalItemsData)
                      : ""} */}
                  </th>
                ))}
              </tr>
            ) : (
              <tr>
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(0) }}
                >
                  {t("teacher.lessonRelated.sum")}
                </th>
                {findByLevels(2).length > 0 && (
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(1) }}
                  ></th>
                )}
                {findByLevels(3).length > 0 && (
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(2) }}
                  ></th>
                )}
                {showEvalPlan && hasEvalPlanData && <th></th>}
                {showEvalPlan && hasEvalPlanData && <th></th>}

                {lessonStudentsData.map((student) => {
                  const calcGrade = student.eval_item_grades
                    ? sumAllNumbersOfObject(student?.eval_item_grades || {})
                    : "";
                  const gradeError = isGradeError(calcGrade);
                  return (
                    <th
                      key={`sum-${student.id}`}
                      className="text-center"
                      style={{ fontSize: "11px" }}
                    >
                      {gradeError ? (
                        <input
                          type="text"
                          value={
                            student.gradeOverride ??
                            (!isGradeError(student.grade)
                              ? student.grade
                              : "") ??
                            ""
                          }
                          onChange={(e) =>
                            handleManualGradeChange(
                              student.id,
                              "grade",
                              e.target.value,
                            )
                          }
                          placeholder={calcGrade}
                          style={{
                            width: "50px",
                            fontSize: "11px",
                            textAlign: "center",
                            border: "1px solid #dc3545",
                            borderRadius: "3px",
                            padding: "1px 2px",
                          }}
                          title="자동 계산 불가 - 수동 입력"
                        />
                      ) : (
                        calcGrade
                      )}
                    </th>
                  );
                })}
              </tr>
            )}
            {checkEvalTypes(["3단평가", "5단평가"]) &&
              lessonSetting.find((r) => r.item_code === "evalArea100")
                .saved_setting !== "원점수기록" && (
                <tr>
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(0) }}
                  >
                    {t("teacher.lessonRelated.average")}
                  </th>
                  {findByLevels(2).length > 0 && (
                    <th
                      className="align-middle"
                      style={{ ...getStickyColumnStyle(1) }}
                    ></th>
                  )}
                  {findByLevels(3).length > 0 && (
                    <th
                      className="align-middle"
                      style={{ ...getStickyColumnStyle(2) }}
                    ></th>
                  )}
                  {showEvalPlan && hasEvalPlanData && <th></th>}
                  {showEvalPlan && hasEvalPlanData && <th></th>}

                  {lessonStudentsData.map((student) => (
                    <th
                      key={`avg-${student.id}`}
                      className="text-center"
                      style={{
                        fontSize: "11px",
                      }}
                    >
                      {student.eval_item_grades
                        ? typeof averageAllNumbersOfObject(
                            student.eval_item_grades,
                          ) === "number"
                          ? averageAllNumbersOfObject(
                              student.eval_item_grades,
                            ).toFixed(1)
                          : ""
                        : ""}
                    </th>
                  ))}
                </tr>
              )}

            {shouldShowConvert100Row &&
              (lessonSetting.find((r) => r.item_code === "evalArea100")
                .saved_setting === "원점수기록" ? (
                <tr>
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(0) }}
                  >
                    {t("teacher.lessonRelated.convert100")}
                  </th>
                  {findByLevels(2).length > 0 && (
                    <th
                      className="align-middle"
                      style={{ ...getStickyColumnStyle(1) }}
                    ></th>
                  )}
                  {findByLevels(3).length > 0 && (
                    <th
                      className="align-middle"
                      style={{ ...getStickyColumnStyle(2) }}
                    ></th>
                  )}
                  {showEvalPlan && hasEvalPlanData && <th></th>}
                  {showEvalPlan && hasEvalPlanData && <th></th>}

                  {lessonStudentsData.map((student) => {
                    const cv = student?.convert100 || "";
                    const cvError = isGradeError(cv);
                    const showConvert100Error =
                      cvError && !manualConvert100Enabled;
                    return (
                      <th
                        key={`convert100-${student.id}`}
                        className="text-center"
                        style={{ fontSize: "11px" }}
                      >
                        {manualConvert100Enabled || cvError ? (
                          <input
                            type="text"
                            value={
                              student.convert100Override ??
                              (!isGradeError(student.convert100)
                                ? student.convert100
                                : "") ??
                              ""
                            }
                            onChange={(e) =>
                              handleManualGradeChange(
                                student.id,
                                "convert100",
                                e.target.value,
                              )
                            }
                            placeholder={showConvert100Error ? cv : ""}
                            style={{
                              width: "50px",
                              fontSize: "11px",
                              textAlign: "center",
                              border: showConvert100Error
                                ? "1px solid #dc3545"
                                : "1px solid #ced4da",
                              borderRadius: "3px",
                              padding: "1px 2px",
                            }}
                            title={
                              showConvert100Error
                                ? "자동 계산 불가 - 수동 입력"
                                : "환산점수 직접 입력"
                            }
                          />
                        ) : (
                          cv
                        )}
                      </th>
                    );
                  })}
                </tr>
              ) : (
                <tr>
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(0) }}
                  >
                    {t("teacher.lessonRelated.convert100")}
                  </th>
                  {findByLevels(2).length > 0 && (
                    <th
                      className="align-middle"
                      style={{ ...getStickyColumnStyle(1) }}
                    ></th>
                  )}
                  {findByLevels(3).length > 0 && (
                    <th
                      className="align-middle"
                      style={{ ...getStickyColumnStyle(2) }}
                    ></th>
                  )}
                  {showEvalPlan && hasEvalPlanData && <th></th>}
                  {showEvalPlan && hasEvalPlanData && <th></th>}

                  {lessonStudentsData.map((student) => {
                    const grade = calculateGradeForStudent(student);
                    const calcCv = calculateConvert100(student, grade);
                    const cvError = isGradeError(calcCv);
                    const showConvert100Error =
                      cvError && !manualConvert100Enabled;
                    const convert100Placeholder = showConvert100Error
                      ? calcCv
                      : cvError
                        ? ""
                        : calcCv;
                    return (
                      <th
                        key={`convert100-${student.id}`}
                        className="text-center"
                        style={{ fontSize: "11px" }}
                      >
                        {manualConvert100Enabled || cvError ? (
                          <input
                            type="text"
                            value={
                              student.convert100Override ??
                              (!isGradeError(calcCv)
                                ? calcCv
                                : !isGradeError(student.convert100)
                                  ? student.convert100
                                  : "") ??
                              ""
                            }
                            onChange={(e) =>
                              handleManualGradeChange(
                                student.id,
                                "convert100",
                                e.target.value,
                              )
                            }
                            placeholder={convert100Placeholder}
                            style={{
                              width: "50px",
                              fontSize: "11px",
                              textAlign: "center",
                              border: showConvert100Error
                                ? "1px solid #dc3545"
                                : "1px solid #ced4da",
                              borderRadius: "3px",
                              padding: "1px 2px",
                            }}
                            title={
                              showConvert100Error
                                ? "자동 계산 불가 - 수동 입력"
                                : "환산점수 직접 입력"
                            }
                          />
                        ) : (
                          calcCv
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            <tr>
              <th
                className="align-middle"
                style={{ ...getStickyColumnStyle(0) }}
              >
                {t("teacher.lessonRelated.specificComment") || "세부특기사항"}
              </th>
              {findByLevels(2).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(1) }}
                ></th>
              )}
              {findByLevels(3).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(2) }}
                ></th>
              )}
              {showEvalPlan && hasEvalPlanData && <th></th>}
              {showEvalPlan && hasEvalPlanData && <th></th>}

              {lessonStudentsData.map((student) => (
                <th
                  key={`comment-${student.id}`}
                  className="text-center"
                  style={{
                    fontSize: "11px",
                    minWidth: "50px",
                  }}
                >
                  <button
                    className={`btn btn-sm ${
                      student.comment ? "btn-success" : "btn-outline-secondary"
                    } ps-1 p-0 m-0`}
                    style={{
                      fontSize: "11px",
                      minWidth: "50px",
                      maxWidth: "60px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      opacity: student.comment ? 0.85 : 1,
                      minHeight: "18px",
                    }}
                    onClick={() =>
                      initiatePromptModal({
                        id: student.id,
                        comment: student.comment,
                        keyName: "comment",
                        title:
                          t("teacher.lessonRelated.specificComment") ||
                          "세부특기사항",
                        fullname: student?.fullname,
                      })
                    }
                  >
                    {student.comment
                      ? student.comment
                      : t("teacher.lessonRelated.empty") || "빈칸"}
                  </button>
                </th>
              ))}
            </tr>

            {schoolLifeflexibleData &&
              schoolLifeflexibleData.find(
                (r) => r.data_value?.schoolLifeComment,
              ) && (
                <tr>
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(0) }}
                  >
                    {t("teacher.lessonRelated.schoolLifeComment") ||
                      "생기부용코멘트"}
                  </th>
                  {findByLevels(2).length > 0 && (
                    <th
                      className="align-middle"
                      style={{ ...getStickyColumnStyle(1) }}
                    ></th>
                  )}
                  {findByLevels(3).length > 0 && (
                    <th
                      className="align-middle"
                      style={{ ...getStickyColumnStyle(2) }}
                    ></th>
                  )}
                  {showEvalPlan && hasEvalPlanData && <th></th>}
                  {showEvalPlan && hasEvalPlanData && <th></th>}

                  {lessonStudentsData.map((student) => (
                    <th
                      key={`comment-${student.id}`}
                      className="text-center"
                      style={{
                        fontSize: "11px",
                        minWidth: "50px",
                      }}
                    >
                      <button
                        className={`btn btn-sm ${
                          student.extra_data?.schoolLifeComment
                            ? "btn-info"
                            : "btn-outline-secondary"
                        } ps-1 p-0 m-0`}
                        style={{
                          fontSize: "11px",
                          minWidth: "50px",
                          maxWidth: "60px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          opacity: student.extra_data?.schoolLifeComment
                            ? 0.85
                            : 1,
                          minHeight: "18px",
                        }}
                        onClick={() =>
                          initiatePromptModal({
                            id: student.id,
                            schoolLifeComment:
                              student.extra_data?.schoolLifeComment,
                            keyName: "schoolLifeComment",
                            title:
                              t("teacher.lessonRelated.schoolLifeComment") ||
                              "생기부용코멘트",
                            upperKey: "extra_data",
                            fullname: student?.fullname,
                          })
                        }
                      >
                        {student.extra_data?.schoolLifeComment
                          ? student.extra_data?.schoolLifeComment
                          : t("teacher.lessonRelated.empty") || "빈칸"}
                      </button>
                    </th>
                  ))}
                </tr>
              )}

            {extraSettingsData?.some(
              (r) =>
                r.sub_category === "researchSummary" && r.data_value === true,
            ) && (
              <tr>
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(0) }}
                >
                  {t("teacher.lessonRelated.researchSummary") || "연구요약"}
                </th>
                {findByLevels(2).length > 0 && (
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(1) }}
                  ></th>
                )}
                {findByLevels(3).length > 0 && (
                  <th
                    className="align-middle"
                    style={{ ...getStickyColumnStyle(2) }}
                  ></th>
                )}
                {showEvalPlan && hasEvalPlanData && <th></th>}
                {showEvalPlan && hasEvalPlanData && <th></th>}

                {lessonStudentsData.map((student) => (
                  <th
                    key={`researchSummary-${student.id}`}
                    className="text-center"
                    style={{
                      fontSize: "11px",
                      minWidth: "50px",
                    }}
                  >
                    <button
                      className={`btn btn-sm ${
                        student.extra_data?.researchSummary
                          ? "btn-info"
                          : "btn-outline-secondary"
                      } ps-1 p-0 m-0`}
                      style={{
                        fontSize: "11px",
                        minWidth: "50px",
                        maxWidth: "60px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        opacity: student.extra_data?.researchSummary ? 0.85 : 1,
                        minHeight: "18px",
                      }}
                      onClick={() =>
                        initiatePromptModal({
                          id: student.id,
                          researchSummary:
                            student.extra_data?.researchSummary,
                          keyName: "researchSummary",
                          title:
                            t("teacher.lessonRelated.researchSummary") ||
                            "연구요약",
                          upperKey: "extra_data",
                          fullname: student?.fullname,
                        })
                      }
                    >
                      {student.extra_data?.researchSummary
                        ? student.extra_data?.researchSummary
                        : t("teacher.lessonRelated.empty") || "빈칸"}
                    </button>
                  </th>
                ))}
              </tr>
            )}

            <tr>
              <th
                className="align-middle"
                style={{ ...getStickyColumnStyle(0) }}
              >
                {t("teacher.lessonRelated.remark") || "비고"}
              </th>
              {findByLevels(2).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(1) }}
                ></th>
              )}
              {findByLevels(3).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(2) }}
                ></th>
              )}
              {showEvalPlan && hasEvalPlanData && <th></th>}
              {showEvalPlan && hasEvalPlanData && <th></th>}

              {lessonStudentsData.map((student) => (
                <th
                  key={`remark-${student.id}`}
                  className="text-center"
                  style={{
                    fontSize: "11px",
                    minWidth: "50px",
                  }}
                >
                  <textarea
                    type="text"
                    rows="3"
                    className="input-like-span"
                    name="extra_data"
                    value={student?.extra_data?.remark || ""}
                    onChange={(e) =>
                      handleTdChange(student.username, "remark", e)
                    }
                  />
                </th>
              ))}
            </tr>
            <tr>
              <th
                className="align-middle"
                style={{ ...getStickyColumnStyle(0) }}
              >
                {t("common.labels.tag")}
              </th>
              {findByLevels(2).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(1) }}
                ></th>
              )}
              {findByLevels(3).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(2) }}
                ></th>
              )}
              {showEvalPlan && hasEvalPlanData && <th></th>}
              {showEvalPlan && hasEvalPlanData && <th></th>}
              {lessonStudentsData.map((student) => (
                <th
                  key={`tag-${student.id}`}
                  className="text-center"
                  style={{
                    minWidth: "50px",
                    fontSize: "11px",
                  }}
                >
                  <div style={{ fontSize: "11px" }}>
                    <div>
                      {student?.tags?.map((tag, tagIndex) => {
                        const tagName = Object.keys(tag)[0];
                        return (
                          <div key={tagIndex}>
                            <div
                              className="text-center"
                              style={{ marginBottom: "1px" }}
                            >
                              {tagName}
                            </div>
                            <select
                              className="form-control p-0"
                              style={{ fontSize: "11px" }}
                              value={tag[tagName]}
                              name="tags"
                              onChange={(e) =>
                                handleTagChange(
                                  tagName,
                                  e,
                                  student.username,
                                  tagIndex,
                                )
                              }
                            >
                              <option value="1">
                                {t("common.labels.veryWeak")}
                              </option>
                              <option value="2">
                                {t("common.labels.weak")}
                              </option>
                              <option value="3">
                                {t("common.labels.normal")}
                              </option>
                              <option value="4">
                                {t("common.labels.strong")}
                              </option>
                              <option value="5">
                                {t("common.labels.veryStrong")}
                              </option>
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
            <tr>
              <th
                className="align-middle"
                style={{ ...getStickyColumnStyle(0) }}
              >
                {t("teacher.lessonRelated.statExclude") || "통계제외"}
              </th>
              {findByLevels(2).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(1) }}
                ></th>
              )}
              {findByLevels(3).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(2) }}
                ></th>
              )}
              {showEvalPlan && hasEvalPlanData && <th></th>}
              {showEvalPlan && hasEvalPlanData && <th></th>}

              {lessonStudentsData.map((student) => (
                <th
                  key={`statExclude-${student.id}`}
                  className="text-center"
                  style={{
                    fontSize: "11px",
                    minWidth: "50px",
                  }}
                >
                  <input
                    type="checkbox"
                    name="extra_data"
                    checked={student?.extra_data?.statExclude === 1 || false}
                    onChange={(e) =>
                      handleTdChange(student.username, "statExclude", e)
                    }
                    className={styles.warningCheckbox}
                    style={{ transform: "scale(1.4)" }}
                  />
                </th>
              ))}
            </tr>
            <tr>
              <th style={{ ...getStickyColumnStyle(0) }}>
                {t("teacher.lessonRelated.deleteButton") || "삭제"}
              </th>
              {findByLevels(2).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(1) }}
                ></th>
              )}
              {findByLevels(3).length > 0 && (
                <th
                  className="align-middle"
                  style={{ ...getStickyColumnStyle(2) }}
                ></th>
              )}
              {showEvalPlan && hasEvalPlanData && <td></td>}
              {showEvalPlan && hasEvalPlanData && <td></td>}
              {lessonStudentsData.map((student) => (
                <td
                  key={`delete-${student.id}`}
                  className="text-center"
                  style={{
                    minWidth: "50px",
                    fontSize: "11px",
                  }}
                >
                  {hasDeletePermission() &&
                    (confirmDelete.includes(student.id) ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleDelete(student)}
                          className="btn btn-sm btn-danger ps-2 pe-2 p-0 m-0"
                          style={{ fontSize: "11px" }}
                        >
                          {t("common.labels.confirm") || "확인"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelDelete()}
                          className="btn btn-sm btn-secondary ps-2 pe-2 p-0 m-0"
                          style={{ fontSize: "11px" }}
                        >
                          {t("common.labels.cancel") || "취소"}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleConfirmDelete(student)}
                        className="btn btn-sm btn-warning ps-2 pe-2 p-0 m-0"
                        style={{ fontSize: "11px" }}
                      >
                        {t("common.labels.delete") || "삭제"}
                      </button>
                    ))}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
        <PromptModal
          isOpen={isModalOpen}
          onClose={closeModal}
          initialState={dataForPromptModal}
          handleSubmit={handlePromptModalSubmit}
          textCount={true}
          closeOnOutsideClick={false}
          placeholder={
            dataForPromptModal?.keyName === "comment"
              ? getTextLengthPlaceholder(
                  textLengthSettings,
                  "subjectComment",
                )
              : dataForPromptModal?.keyName === "schoolLifeComment"
                ? getTextLengthPlaceholder(
                    textLengthSettings,
                    "schoolLifeSubjectComment",
                  )
                : ""
          }
          flexibleDataForTextLimit={textLengthSettings}
          fieldNameForTextLimit={
            dataForPromptModal?.keyName === "comment"
              ? "subjectComment"
              : dataForPromptModal?.keyName === "schoolLifeComment"
                ? "schoolLifeSubjectComment"
                : undefined
          }
        />
      </div>
    </div>
  );
}
